import React from "react";
import KpiCard from "../KpiCard";
import { ConfidencePill } from "../Pills";
import { fmt } from "../../lib/format";
import {
  Sun, TrendingDown, TrendingUp, Activity, Zap, Gauge, Droplets,
} from "lucide-react";

/**
 * Seven-card energy overview.
 *
 * The first row ("Today") sources its values from `today`, computed by
 * the backend's `_compute_energy_summary(period="today")` — the SAME
 * code path the Energy page's Today tab uses, so the two views are
 * guaranteed to agree numerically. A regression test pins this
 * invariant: `dashboard.energy_today.import_kwh ==
 * energy/summary?period=today.import_kwh` for any given site/day.
 *
 * The second row keeps the 30-day rollup figures used by
 * `aggregate_energy_kpis` (self-consumption %, grid autonomy %, pump
 * total) — those numbers only make sense over a window.
 *
 * @param {object} props
 * @param {object} props.kpis - 30-day aggregated KPIs.
 * @param {object|null} props.today - today-only KPI block from
 *   `/sites/{id}/dashboard.energy_today`. Null while loading or if the
 *   backend couldn't compute (e.g. unknown timezone).
 * @param {object} props.confidence - per-domain confidence levels.
 */
export default function EnergySection({ kpis, today, confidence }) {
  // Solar pill falls back to the energy domain when no dedicated solar data
  // source is configured — both are equally valid signals at this level.
  const solarPill = confidence.solar !== "unknown" ? confidence.solar : confidence.energy;

  return (
    <section className="space-y-3" data-testid="energy-section">
      <div className="flex items-center gap-3">
        <h2 className="label-tiny flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#F59E0B]" /> Energy
        </h2>
        <ConfidencePill level={confidence.energy} />
      </div>

      {/* Today row — matches Energy page Today tab exactly. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="energy-today-row">
        <KpiCard
          label="Today's PV"
          value={fmt(today?.pv_production_kwh, 1)}
          unit="kWh"
          Icon={Sun}
          accent="amber"
          confidence={solarPill}
          sub="Today (site local)"
          data-testid="energy-today-pv"
        />
        <KpiCard
          label="Today's Import"
          value={fmt(today?.import_kwh, 2)}
          unit="kWh"
          Icon={TrendingDown}
          accent="red"
          sub="Today (site local)"
          data-testid="energy-today-import"
        />
        <KpiCard
          label="Today's Export"
          value={fmt(today?.export_kwh, 2)}
          unit="kWh"
          Icon={TrendingUp}
          accent="green"
          sub="Today (site local)"
          data-testid="energy-today-export"
        />
        <KpiCard
          label="Today's Pump"
          value={fmt(today?.waterpump_kwh, 2)}
          unit="kWh"
          Icon={Droplets}
          accent="cyan"
          sub="Today (site local)"
          data-testid="energy-today-pump"
        />
      </div>

      {/* 30-day rollup row — averages and percentages only make sense over
          a window, so the section label clarifies the timeframe. */}
      <div className="flex items-center gap-2 pt-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Last {kpis.days || 30} days
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="energy-window-row">
        <KpiCard label="Site Consumption" value={fmt(kpis.site_consumption_kwh, 1)} unit="kWh" Icon={Activity} accent="default" sub="derived from P1 + PV" />
        <KpiCard label="Self-Consumption" value={fmt(kpis.self_consumption_pct, 0)} unit="%" Icon={Gauge} accent="amber" sub={`${fmt(kpis.self_consumption_kwh, 1)} kWh used on-site`} />
        <KpiCard label="Grid Autonomy" value={fmt(kpis.grid_autonomy_pct, 0)} unit="%" Icon={Gauge} accent="green" sub={`${kpis.days} day average`} />
      </div>
    </section>
  );
}
